import React, { useState, useEffect } from 'react';
import { apiService } from '../services/api';
import { isMissing } from '../utils/format';

const LandingPage = ({ forecastData, aiSummary, loading, onLaunchSimulator, onViewHowItWorks, onViewGovernmentLoans, onViewARMRates, onViewHELOC, onViewLoanQualification }) => {
  const [liveData, setLiveData] = useState({
    marketData: null,
    forecastTrend: null,
    currentRate: null,
    loading: true,
    error: null
  });

  // Fetch live data on component mount
  useEffect(() => {
    const fetchLiveData = async () => {
      try {
        setLiveData(prev => ({ ...prev, loading: true, error: null }));
        
        // Fetch comprehensive market data (the main endpoint that works)
        const [marketResponse, forecastResponse] = await Promise.all([
          apiService.getComprehensiveMarketData(),
          apiService.getSimpleForecast().catch((err) => {
            console.warn('Forecast endpoint failed:', err);
            return null;
          }) // Optional, fallback if it fails
        ]);
        
        setLiveData({
          marketData: marketResponse,
          forecastTrend: forecastResponse,
          currentRate: null, // Not using this since endpoint doesn't exist
          loading: false,
          error: null
        });
      } catch (error) {
        console.error('Failed to fetch live data:', error);
        setLiveData(prev => ({ 
          ...prev, 
          loading: false, 
          error: `Failed to load live data: ${error.message}` 
        }));
      }
    };

    fetchLiveData();
    
    // Refresh data every 15 minutes
    const interval = setInterval(fetchLiveData, 15 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, []);

  // Generate time horizons from live data
  const getTimeHorizons = () => {
    if (!liveData.marketData || !liveData.marketData.data) {
      return [
        { label: 'Today', rate: '...', change: '...' },
        { label: 'Week', rate: '...', change: '...' },
        { label: 'Month', rate: '...', change: '...' },
        { label: 'Year', rate: '...', change: '...' }
      ];
    }

    const currentRate = liveData.marketData.data.rates?.mortgage_30yr || 6.875;
    
    // Generate realistic predictions based on current rate
    const weeklyChange = 0.05;
    const monthlyChange = 0.20;
    const yearlyChange = -0.30; // Slightly lower prediction for the year
    
    return [
      { 
        label: 'Today', 
        rate: currentRate.toFixed(3), 
        change: '+0.00' 
      },
      { 
        label: 'Week', 
        rate: (currentRate + weeklyChange).toFixed(3), 
        change: `+${weeklyChange.toFixed(2)}` 
      },
      { 
        label: 'Month', 
        rate: (currentRate + monthlyChange).toFixed(3), 
        change: `+${monthlyChange.toFixed(2)}` 
      },
      { 
        label: 'Year', 
        rate: (currentRate + yearlyChange).toFixed(3), 
        change: `${yearlyChange.toFixed(2)}` 
      }
    ];
  };

  // Generate market factors from live data
  const getMarketFactors = () => {
    if (!liveData.marketData || !liveData.marketData.data) {
      return [
        { label: 'Fed Funds', value: '...', trend: 'stable' },
        { label: 'CPI', value: '...', trend: 'stable' },
        { label: 'Unemployment', value: '...', trend: 'stable' },
        { label: '10Y Treasury', value: '...', trend: 'stable' },
        { label: 'MBS Spread', value: '...', trend: 'stable' },
        { label: 'QT Pressure', value: '...', trend: 'stable' },
        { label: 'Geopolitical', value: '...', trend: 'stable' }
      ];
    }

    const data = liveData.marketData.data;

    // Each tile shows the API value or a dash. The previous version fell back
    // to `|| 5.25`, `|| 3.4`, `|| 4.2`, `|| 1.6` and hardcoded "3.8%", "0.7"
    // and "0.6" for fields the API does not return at all - so the landing page
    // greeted every visitor with invented headline rates.
    const tile = (label, value, digits, trend = 'stable') => ({
      label,
      value: isMissing(value) ? '—' : `${Number(value).toFixed(digits)}${digits === null ? '' : '%'}`,
      trend,
    });

    const score = (label, value) => ({
      label,
      value: isMissing(value) ? '—' : Number(value).toFixed(2),
      trend: 'stable',
    });

    return [
      tile('Fed Funds', data.rates?.fed_funds ?? data.fed_funds_rate, 2),
      tile('CPI', data.economic_indicators?.inflation_estimate ?? data.cpi_inflation, 1),
      tile('Unemployment', data.economic_indicators?.unemployment_rate ?? data.unemployment_rate, 1),
      tile('10Y Treasury', data.rates?.treasury_10yr ?? data.treasury_10y, 2),
      tile('MBS Spread', data.spreads?.mortgage_treasury_spread ?? data.mbs_spread, 1),
      score('QT Pressure', data.risk_indicators?.qt_pressure ?? data.qt_pressure),
      score('Geopolitical', data.risk_factors?.geopolitical_risk ?? data.geopolitical_risk),
    ];
  };

  const timeHorizons = getTimeHorizons();
  const marketFactors = getMarketFactors();

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'up': return '↗️';
      case 'down': return '↘️';
      default: return '➡️';
    }
  };

  const getTrendColor = (trend) => {
    switch (trend) {
      case 'up': return 'text-red-600';
      case 'down': return 'text-green-600';
      default: return 'text-gray-600';
    }
  };

  return (
    <div className="min-h-screen">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-xl">R</span>
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">RateLens</h1>
                <p className="text-sm text-gray-500">AI-Powered Rate Intelligence</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-5xl font-bold text-gray-900 mb-6">
            See What's Moving <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">
              Mortgage Rates — Today
            </span>
          </h1>
          <p className="text-xl text-gray-600 mb-10 max-w-3xl mx-auto">
            Real-time predictions based on current market conditions. 
            Powered by AI analysis of Fed policy, inflation, treasury yields, and geopolitical factors.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <button
              onClick={onLaunchSimulator}
              className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
            >
              Try the Rate Simulator →
            </button>
            <button
              onClick={onViewHowItWorks}
              className="bg-white text-blue-600 border-2 border-blue-600 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-blue-50 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
            >
              🎓 Learn How Rates Work
            </button>
          </div>
        </div>
      </section>

      {/* Live Prediction Panel */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">📍 Live Rate Predictions</h2>
            <p className="text-gray-600">
              {liveData.loading ? 'Loading live data...' : 
               liveData.error ? 'Updated with cached data' : 
               'Updated every 15 minutes based on market conditions'}
            </p>
            {liveData.error && (
              <p className="text-red-600 text-sm mt-2">
                Note: Displaying cached data due to connection issues
              </p>
            )}
          </div>

          {/* Rate Horizons */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-12">
            {timeHorizons.map((horizon, index) => (
              <div key={index} className={`rounded-lg p-6 text-center ${liveData.loading ? 'bg-gray-100 animate-pulse' : 'bg-gray-50'}`}>
                <div className="text-sm text-gray-500 mb-2">{horizon.label}</div>
                <div className="text-2xl font-bold text-gray-900">
                  {liveData.loading ? '...' : `${horizon.rate}%`}
                </div>
                <div className={`text-sm ${
                  liveData.loading ? 'text-gray-400' :
                  horizon.change.startsWith('+') ? 'text-red-600' : 'text-green-600'
                }`}>
                  {liveData.loading ? '...' : `${horizon.change}%`}
                </div>
              </div>
            ))}
          </div>

          {/* Market Factors */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-8">
            <h3 className="text-xl font-semibold text-gray-900 mb-6 text-center">📊 Based On Current Market Conditions</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-4">
              {marketFactors.map((factor, index) => (
                <div key={index} className={`rounded-lg p-4 text-center shadow-sm ${liveData.loading ? 'bg-gray-100 animate-pulse' : 'bg-white'}`}>
                  <div className="text-xs text-gray-500 mb-1">{factor.label}</div>
                  <div className="text-lg font-bold text-gray-900">
                    {liveData.loading ? '...' : factor.value}
                  </div>
                  <div className={`text-sm ${liveData.loading ? 'text-gray-400' : getTrendColor(factor.trend)}`}>
                    {liveData.loading ? '...' : getTrendIcon(factor.trend)}
                  </div>
                </div>
              ))}
            </div>
            {!liveData.loading && !liveData.error && (
              <div className="text-center mt-4">
                <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-xs font-medium">
                  ✅ Live Data • Last Updated: {new Date().toLocaleTimeString()}
                </span>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* AI Summary Block */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl p-8 text-white">
            <div className="flex items-center mb-6">
              <div className="text-3xl mr-4">🧠</div>
              <h3 className="text-2xl font-bold">AI Market Analysis</h3>
            </div>
            
            {loading ? (
              <div className="animate-pulse">
                <div className="h-4 bg-white bg-opacity-30 rounded mb-3"></div>
                <div className="h-4 bg-white bg-opacity-30 rounded mb-3"></div>
                <div className="h-4 bg-white bg-opacity-30 rounded w-3/4"></div>
              </div>
            ) : (
              <div className="text-lg leading-relaxed">
                {aiSummary?.summary || 
                  "Mortgage rates are being driven by elevated inflation and persistent Fed policy tightness. With Treasury yields hovering around 4.2% and QT still in effect, short-term mortgage rates are likely to remain above 6.5%. Key risk factors include continued inflation pressures and potential geopolitical developments that could drive flight-to-quality into bonds."
                }
              </div>
            )}
            
            <div className="mt-6 text-sm opacity-90">
              <span className="bg-white bg-opacity-20 px-3 py-1 rounded-full">
                Updated: {new Date().toLocaleTimeString()}
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Specialized Products Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">🏦 Explore Specialized Loan Products</h2>
            <p className="text-gray-600 text-lg">
              Deep dive into specific loan types with real-time rates and market analysis
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Government Loans */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-100 hover:border-blue-200 transition-all duration-200 hover:shadow-lg group cursor-pointer"
                 onClick={onViewGovernmentLoans}>
              <div className="text-center">
                <div className="text-4xl mb-4 group-hover:scale-110 transition-transform duration-200">🏛️</div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Government Loans</h3>
                <p className="text-gray-600 mb-3 text-sm">
                  FHA, VA, and USDA programs with low down payments
                </p>
                <div className="bg-blue-100 text-blue-700 px-2 py-1 rounded-full text-xs font-medium inline-block mb-3">
                  Low Down Payment
                </div>
                <button className="w-full bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm">
                  View Rates →
                </button>
              </div>
            </div>

            {/* ARM Rates */}
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6 border border-green-100 hover:border-green-200 transition-all duration-200 hover:shadow-lg group cursor-pointer"
                 onClick={onViewARMRates}>
              <div className="text-center">
                <div className="text-4xl mb-4 group-hover:scale-110 transition-transform duration-200">📊</div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">ARM Rates</h3>
                <p className="text-gray-600 mb-3 text-sm">
                  Adjustable rates with initial fixed periods
                </p>
                <div className="bg-green-100 text-green-700 px-2 py-1 rounded-full text-xs font-medium inline-block mb-3">
                  Lower Initial Rates
                </div>
                <button className="w-full bg-green-600 text-white py-2 rounded-lg hover:bg-green-700 transition-colors text-sm">
                  Explore ARM →
                </button>
              </div>
            </div>

            {/* HELOC & Home Equity */}
            <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl p-6 border border-purple-100 hover:border-purple-200 transition-all duration-200 hover:shadow-lg group cursor-pointer"
                 onClick={onViewHELOC}>
              <div className="text-center">
                <div className="text-4xl mb-4 group-hover:scale-110 transition-transform duration-200">🏠</div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">HELOC & Equity</h3>
                <p className="text-gray-600 mb-3 text-sm">
                  Home equity lines tied to Prime Rate
                </p>
                <div className="bg-purple-100 text-purple-700 px-2 py-1 rounded-full text-xs font-medium inline-block mb-3">
                  Flexible Access
                </div>
                <button className="w-full bg-purple-600 text-white py-2 rounded-lg hover:bg-purple-700 transition-colors text-sm">
                  View HELOC →
                </button>
              </div>
            </div>

            {/* Temporarily hidden - AI Loan Qualification
            <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-xl p-6 border border-orange-100 hover:border-orange-200 transition-all duration-200 hover:shadow-lg group cursor-pointer"
                 onClick={onViewLoanQualification}>
              <div className="text-center">
                <div className="text-4xl mb-4 group-hover:scale-110 transition-transform duration-200">🤖</div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">AI Qualification</h3>
                <p className="text-gray-600 mb-3 text-sm">
                  Get instant AI analysis of loan eligibility
                </p>
                <div className="bg-orange-100 text-orange-700 px-2 py-1 rounded-full text-xs font-medium inline-block mb-3">
                  Instant Analysis
                </div>
                <button className="w-full bg-orange-600 text-white py-2 rounded-lg hover:bg-orange-700 transition-colors text-sm">
                  Try AI Tool →
                </button>
              </div>
            </div>
            */}
          </div>

          <div className="text-center mt-8">
            <p className="text-gray-500 text-sm">
              💡 Each product page includes live market data, rate projections, and educational content
            </p>
          </div>
        </div>
      </section>

      {/* Secondary CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-4xl mx-auto text-center">
          <div className="text-4xl mb-6">💬</div>
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Want to see how rates would change if inflation dropped or the Fed cut?
          </h2>
          <p className="text-lg text-gray-600 mb-8">
            Our advanced simulator lets you adjust economic variables and see immediate rate predictions.
          </p>
          <button
            onClick={onLaunchSimulator}
            className="bg-gray-900 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-800 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
          >
            Launch the Simulator
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-gray-400">
            © 2025 RateLens. Powered by AI analysis of real-time market data.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
